/**
 * Language detection for the i18n layer.
 *
 * Resolution order:
 *   1. `?lang=XX` query param (also written to localStorage)
 *   2. Stored choice from a prior session (localStorage)
 *   3. `navigator.language` (e.g. `uk-UA` → `uk`, `en-US` → `en`)
 *   4. Default to `'en'`
 *
 * Mirrors the country override pattern in `src/utils/countryCode.ts`.
 */

export const SUPPORTED_LANGS = ['en', 'uk'] as const;

export type Lang = (typeof SUPPORTED_LANGS)[number];

const STORAGE_KEY = 'doyourstep.lang';

function isSupported(code: string | null | undefined): code is Lang {
    return !!code && (SUPPORTED_LANGS as readonly string[]).includes(code.toLowerCase());
}

function fromUrl(): Lang | null {
    if (typeof window === 'undefined') return null;
    try {
        const v = new URLSearchParams(window.location.search).get('lang');

        if (isSupported(v)) {
            const code = v.toLowerCase() as Lang;

            window.localStorage.setItem(STORAGE_KEY, code);

            return code;
        }
    } catch {
        /* ignore */
    }

    return null;
}

function fromStorage(): Lang | null {
    if (typeof window === 'undefined') return null;
    try {
        const v = window.localStorage.getItem(STORAGE_KEY);

        return isSupported(v) ? (v.toLowerCase() as Lang) : null;
    } catch {
        return null;
    }
}

function fromNavigator(): Lang | null {
    if (typeof navigator === 'undefined') return null;
    const v = (navigator.language || '').slice(0, 2).toLowerCase();

    return isSupported(v) ? (v as Lang) : null;
}

export function detectLanguage(): Lang {
    return fromUrl() || fromStorage() || fromNavigator() || 'en';
}

export function persistLanguage(lang: Lang): void {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
        /* ignore */
    }
}

/** Map our language code to a BCP-47 tag for `Intl.*` formatters. */
export function langToBcp47(lang: Lang): string {
    switch (lang) {
        case 'uk':
            return 'uk-UA';
        case 'en':
        default:
            return 'en-US';
    }
}

export type PluralForm = 'one' | 'few' | 'many';

/**
 * Which plural form a count takes.
 *
 * Ukrainian has three: 1 крок · 2–4 кроки · 5 кроків, with 11–14 taking the
 * last form regardless of the final digit. English has two, so everything but
 * exactly 1 answers `many` — bundles then carry only `one` and `many`.
 */
export function pluralForm(lang: Lang, n: number): PluralForm {
    const abs = Math.abs(n);

    if (lang !== 'uk') return abs === 1 ? 'one' : 'many';

    const tens = abs % 100;
    const unit = abs % 10;

    if (tens >= 11 && tens <= 14) return 'many';
    if (unit === 1) return 'one';
    if (unit >= 2 && unit <= 4) return 'few';
    return 'many';
}

/** `formatDate(lang, d, { day: 'numeric', month: 'long' })` — never a hardcoded locale. */
export function formatDate(lang: Lang, date: Date, opts: Intl.DateTimeFormatOptions): string {
    try {
        return date.toLocaleDateString(langToBcp47(lang), opts);
    } catch {
        return date.toLocaleDateString();
    }
}
